import type { TypeImageBannerDetailed } from 'types/autogen/contentful';
import Image from './Image';
import Picture from './Picture';
import {
    forwardRef,
    type ReactNode,
    type HTMLAttributes,
    type ImgHTMLAttributes,
    useState,
    useEffect,
    useRef
} from 'react';
import ImageWrapper from './ImageWrapper';
import { useContentfulInspectorMode } from '@contentful/live-preview/react';
import RichText from '../shared/RichText';
import type { Document } from '@contentful/rich-text-types';
import { Spacing12 } from 'autogen/design-tokens/tokens';
import styled from 'styled-components';
import WithAction from '../shared/WithAction';
import type { Breakpoint } from 'variables';

const Figure = styled.figure`
    display: flex;
    flex-direction: column;
    gap: ${Spacing12};
    margin-bottom: 0;
`;

const StyledPageAction = styled(WithAction)`
    &:hover {
        text-decoration: none;
    }
`;

const ImageRenderer = forwardRef<HTMLImageElement, TypeImageBannerDetailed & ImgHTMLAttributes<HTMLImageElement>>(
    (props, ref) => {
        const { sys, metadata, fields, ...restProps } = props;
        const { desktopImage, image } = fields;
        if (!image) return;

        if (image && desktopImage) {
            return <Picture ref={ref} image={image} desktopImage={desktopImage} {...restProps} />;
        }

        return <Image ref={ref} style={{ width: '100%' }} {...image} {...restProps} />;
    }
);

function WithCaption(props: {
    caption?: Document;
    inspectorProps?: ReturnType<typeof useContentfulInspectorMode>;
    children: ReactNode;
}) {
    const { caption, inspectorProps, children } = props;

    return caption ? (
        <Figure>
            {children}
            <figcaption {...inspectorProps?.({ fieldId: 'caption' })}>
                <RichText {...caption} />
            </figcaption>
        </Figure>
    ) : (
        children
    );
}

const ImageBanner = forwardRef((props: TypeImageBannerDetailed & HTMLAttributes<HTMLDivElement>, ref: any) => {
    const {
        fields,
        sys,
        metadata,
        sys: { id },
        ...restProps
    } = props;
    const { buttonText, buttonPosition, quickviewContent, theme, textOnImage, link, disableLazyload, caption } = fields;
    const desktopBreakpoint: Breakpoint = '1025px';

    //TODO: We need to balance having the smallest default while minimising the amount of times that we have to request a bigger image.
    const [sizes, setSizes] = useState(`(min-width: ${desktopBreakpoint}) 25vw, 50vw`);
    const imageRef = useRef<HTMLImageElement | null>(null);
    useEffect(() => {
        // The image is initially stretched to fill the required space, and its size is checked afterward.
        // If necessary, a higher resolution image will be fetched based on the actual size.
        const imageWidth = imageRef.current?.offsetWidth;
        if (!imageWidth) return;

        setSizes(`${imageWidth}px`);
    }, []);

    const inspectorProps = useContentfulInspectorMode({ entryId: id });
    const dialogOptions =
        buttonText && quickviewContent && buttonPosition
            ? {
                  buttonText: buttonText,
                  quickviewContent: quickviewContent,
                  buttonPosition: buttonPosition
              }
            : undefined;

    if (dialogOptions || theme || textOnImage) {
        return (
            <StyledPageAction link={link}>
                <WithCaption caption={caption} inspectorProps={inspectorProps}>
                    <ImageWrapper dialog={dialogOptions} theme={theme} textOnImage={textOnImage} id={id} {...restProps}>
                        <ImageRenderer
                            ref={imageRef}
                            sizes={sizes}
                            loading={disableLazyload ? 'eager' : 'lazy'}
                            {...props}
                            {...inspectorProps({ fieldId: 'image' })}
                        />
                    </ImageWrapper>
                </WithCaption>
            </StyledPageAction>
        );
    }

    return (
        <StyledPageAction link={link}>
            <WithCaption caption={caption} inspectorProps={inspectorProps}>
                <div {...restProps}>
                    <ImageRenderer
                        ref={imageRef}
                        sizes={sizes}
                        loading={disableLazyload ? 'eager' : 'lazy'}
                        {...props}
                        {...inspectorProps({ fieldId: 'image' })}
                    />
                </div>
            </WithCaption>
        </StyledPageAction>
    );
});

export default ImageBanner;
